import {
    CircularProgress,
    DialogContent,
    DialogTitle,
    Grid,
    TextField
} from "@mui/material";
import {Dialog2} from "../../misc/Dialog2";
import {SmoothActionBar} from "../../misc/SmoothActionBar";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {useAsync2, useAsyncAction} from "nate-react-api-helpers";
import {api} from "../../api/API";
import {Product} from "../../api/Products";
import {FindOrCreateCompany} from "../project/manage/summary/FindOrCreateCompany";
import {Company} from "../../api/Companies";
import {DimensionInput2} from "./DimensionInput2";
import {
    doorThicknesses, frameConstruction, frameGauge, frameMaterial, handingOptions
} from "../project/quote/openings/QuoteOpenings";
import {
    hollowMetalFrame
} from "../project/quote/openings/Const";
import {SelectFiltered} from "./SelectFiltered";
import {dummySelectWrapper} from "../../misc/scroller/Table";
import {
    frameBuilderPreps,
    frameHingePreps,
    frameStrikePreps,
    hasAnchorPrep,
    StandardPrepSelector
} from "./StandardPrepSelector";
import {ImperialDistance} from "../../misc/ImperialDistance";
import {DropDownContext, useDropdownData} from "../project/settings/DropDowns";

export function FrameBuilder(props: {
    components: boolean;
    onDone(v: Product): void;
    onCancel(): void;
    simpleQuoteMode?: boolean;
}) {

    const act = useAsyncAction(async (input) => {
        const p = await api.products.buildFrameSpec(input)
        props.onDone(p)
    }, [])

    const [company, setCompany] = useState<Company|null>(null);
    const [preps, setPreps] = useState(["4.5\" STD"]);
    const [anchorQty, setAnchorQty] = useState("0");
    const [handing, setHanding] = useState("");
    const [height, setHeight] = useState("");
    const [width, setWidth] = useState("");
    const [jambDepth, setJambDepth] = useState("");
    const [doorThickness, setDoorThickness] = useState("");
    const [material, setMaterial] = useState("");
    const [series, setSeries] = useState("HM Frame");
    const [gauge, setGauge] = useState("");
    const [profile, setProfile] = useState("STD"); // always STD
    const [construction, setConstruction] = useState("");
    const [category, setCategory] = useState("frame");
    const [openingType, setOpeningType] = useState("Single");

    const sys = useMemo(() => {
        if(width.toLowerCase().trim().endsWith("mm")) {
            return "metric"
        }

        return undefined;
    }, [width]);

    const companySearch = useAsync2(() => api.companies.list({kind: "supplier", search: "Tykel", offset: 0}), {}, [])
    const tykelCos = companySearch.result?.data;
    useEffect(() => {
        if(tykelCos?.length) {
            setCompany(tykelCos[0])
        }
    }, [tykelCos]);

    const disableWidth = category === hingeJambCat || category === strikeJambCat;
    const disableHeight = category === headerCat;
    const disableHanding = category === headerCat;
    const disableConstruction = category === hingeJambCat || category === strikeJambCat || category === headerCat;
    const disableHingePrep = category === strikeJambCat || category === headerCat;
    const disableStrikePrep = category === hingeJambCat || category === headerCat;
    const disabledOpeningType = category !== "frame";
    const simpleQuoteMode = !!props.simpleQuoteMode;

    useReset(disabledOpeningType, setOpeningType, "Single");
    useReset(disableWidth, setWidth, "");
    useReset(disableHeight, setHeight, "");
    useReset(disableHanding, setHanding, "");
    useReset(disableConstruction, setConstruction, "");
    useEffect(() => {
        if(disableHingePrep || disableStrikePrep || simpleQuoteMode) {
            setPreps([])
        }
    }, [disableHingePrep, disableStrikePrep, simpleQuoteMode]);

    const ddl = useContext(DropDownContext);
    const frameProfileOptions = useDropdownData(ddl.frameProfiles);

    if(ddl.loading) {
        return <Dialog2 open><CircularProgress /></Dialog2>
    }

    const submit = () => {
        act.callback({
            manufacturer: company?.id || 0,
            width: removeZero(width),
            height: removeZero(height),
            handing,
            series, material, gauge, profile, jambDepth,
            construction,
            anchorQty: anchorQty ? parseInt(anchorQty) : 0,
            preps,
            category: category === "frame" ? undefined : category,
            type: category === "frame" ? openingType : "",
            doorThickness,
        })
    };

    return (
        <Dialog2 open onClose={props.onCancel} onEnterPress={submit}>
            <DialogTitle>Frame Builder</DialogTitle>
            <DialogContent>
                <div style={{height: 8}}/>
                <FindOrCreateCompany value={company} kind={"supplier"} label={"Manufacturer Name"}
                                     onChange={setCompany}/>

                <div style={{height: 8}}/>
                {props.components && <>
                    <SelectFiltered label="Type"
                                    value={category} onChange={setCategory}
                                    options={typeOptions}/>
                    <div style={{height: 8}}/>
                </>}

                <Grid container spacing={1} flexDirection="row">
                    <Grid item xs={4}>
                        <DimensionInput2 label="Width" value={width} onChange={setWidth} disabled={disableWidth}/>
                    </Grid>
                    <Grid item xs={4}>
                        <DimensionInput2 label="Height" value={height} onChange={setHeight}
                                         disabled={disableHeight}/>
                    </Grid>
                    {category !== "frame" && <Grid item xs={4}>
                        <SelectFiltered
                            label="Handing" value={handing} onChange={setHanding}
                            disabled={disableHanding}
                            options={["LH", "RH"].map(v => ({value: v, display: v}))}/>
                    </Grid>}
                </Grid>

                {category === "frame" && <>
                    <div style={{height: 8}}/>
                    <Grid container spacing={1} flexDirection="row">
                        <Grid item xs={4}>
                            <SelectFiltered
                                label="Type" value={openingType} onChange={setOpeningType}
                                disabled={disabledOpeningType}
                                options={["Single", "Pair"].map(v => ({value: v, display: v}))}/>
                        </Grid>
                        {!props.simpleQuoteMode && <Grid item xs={4}>
                            <SelectFiltered
                                label="Handing" value={handing} onChange={setHanding}
                                disabled={disableHanding}
                                options={handingOptions(dummySelectWrapper({openingType: openingType}))}/>
                        </Grid>}
                    </Grid>
                </>}

                <div style={{height: 8}}/>

                <Grid container spacing={1}>
                    <Grid item xs={4}>
                        <SelectFiltered label="Series" value={series} onChange={setSeries}
                                        options={[hollowMetalFrame].map(v => ({value: v, display: v}))}/>
                    </Grid>
                    <Grid item xs={4}>
                        <SelectFiltered label="Material" value={material} onChange={setMaterial}
                                        options={frameMaterial(dummySelectWrapper({frameSeries: series}))}/>
                    </Grid>
                    <Grid item xs={4}>
                        <SelectFiltered label="Gauge" value={gauge} onChange={setGauge}
                                        options={frameGauge(dummySelectWrapper({frameSeries: series}))}/>
                    </Grid>
                </Grid>

                <div style={{height: 8}}/>

                <Grid container spacing={1} flexDirection="row">
                    <Grid item xs={4}>
                        <DimensionInput2 label="Jamb Depth" inches value={jambDepth} onChange={setJambDepth}/>
                    </Grid>
                    <Grid item xs={4}>
                        <SelectFiltered label="Door Thickness" value={doorThickness} onChange={setDoorThickness}
                                        options={doorThicknesses(sys).map(v => ({value: v, display: v}))}/>
                    </Grid>
                </Grid>

                <div style={{height: 8}}/>

                <Grid container spacing={1} flexDirection="row">
                    <Grid item xs={4}>
                        <SelectFiltered label="Construction"
                                        disabled={disableConstruction}
                                        value={construction} onChange={setConstruction}
                                        options={frameConstruction(dummySelectWrapper({frameSeries: series}))}/>
                    </Grid>
                    <Grid item xs={4}>
                        <SelectFiltered label="Profile" value={profile} onChange={setProfile}
                                        options={frameProfileOptions}/>
                    </Grid>
                </Grid>

                <div style={{height: 8}}/>

                <Grid container spacing={1}>
                    {!props.simpleQuoteMode && <Grid item xs={8}>
                        <StandardPrepSelector
                            fullWidth
                            optionList={
                                category === "frame" ? frameBuilderPreps :
                                    category === hingeJambCat ? frameHingePreps :
                                        category === strikeJambCat ? frameStrikePreps : []
                            }
                            preps={preps} onChange={setPreps}/>
                    </Grid>}
                    <Grid item xs={4}>
                        {hasAnchorPrep(preps) && <TextField
                            fullWidth
                            label="Anchor Qty"
                            InputLabelProps={{shrink: true}}
                            size="small"
                            variant="outlined"
                            value={anchorQty}
                            onBlur={e => {
                                try {
                                    setAnchorQty(parseInt(e.target.value).toString())
                                } catch (e) {
                                }
                            }}
                            onChange={e => setAnchorQty(e.target.value)}
                        />}
                    </Grid>
                </Grid>
            </DialogContent>
            <SmoothActionBar
                loading={act.loading}
                error={act.error}
                actionName="Confirm"
                onCancel={props.onCancel}
                onSubmit={submit}
            />
        </Dialog2>
    )
}

// value corresponds to product_category name
const hingeJambCat = "Hinge Jamb"
const strikeJambCat = "Strike Jamb"
const headerCat = "Header"

const typeOptions = [
    {value: "frame", display: "Frame"},
    {value: hingeJambCat, display: hingeJambCat},
    {value: strikeJambCat, display: strikeJambCat},
    {value: headerCat, display: headerCat},
]

function removeZero(value: string) {
    if(value === "") return value;
    const v = ImperialDistance.parse(value)
    if(v.getValue() === 0) return "";

    return value;
}

function useReset(reset: boolean, setState: (v: string) => void, value: string) {
    useEffect(() => {
        if(reset) setState(value)
    }, [reset, setState, value])
}